export type ExplodeChain = number[]; // e.g. [6,6,3]
export type ExplodeResult = {
    chains: ExplodeChain[]; // one chain per initial die
    timestamp: number;
};

function secureRandomInt(max: number): number {
    if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
        const array = new Uint32Array(1);
        crypto.getRandomValues(array);
        return array[0] % max;
    }
    return Math.floor(Math.random() * max);
}

/**
 * Roll N dice with a given number of sides.
 * When exploding=true, a roll of the max face generates an extra die (chain).
 * Caps global rolls to avoid runaway generation.
 */
export function rollDice(
    initialCount: number,
    sides: number,
    exploding: boolean,
    maxInitial = 20,
    globalCap = 1000
): ExplodeResult {
    const safeSides = Math.max(2, Math.floor(sides));
    const safeInitial = Math.max(0, Math.min(initialCount, maxInitial));
    const chains: ExplodeChain[] = [];
    let globalRolls = 0;

    for (let i = 0; i < safeInitial; i++) {
        if (globalRolls >= globalCap) break;
        const chain: number[] = [];
        let r = (secureRandomInt(safeSides) % safeSides) + 1;
        chain.push(r);
        globalRolls++;
        while (exploding && r === safeSides && globalRolls < globalCap) {
            r = (secureRandomInt(safeSides) % safeSides) + 1;
            chain.push(r);
            globalRolls++;
        }
        chains.push(chain);
    }

    return { chains, timestamp: Date.now() };
}

/**
 * Roll N exploding d6 (Brío system default).
 */
export function rollExplodingDice(
    initialCount: number,
    maxInitial = 20,
    globalCap = 1000
): ExplodeResult {
    return rollDice(initialCount, 6, true, maxInitial, globalCap);
}